import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { useSupplier } from "../../context/SupplierContext";
import HubModeNotice from "../../components/HubModeNotice";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  ShoppingCart, ArrowRight, Package, Building2, X, Download, Upload,
  MessageSquare, ChevronLeft, ChevronRight, Plug, Store,
} from "lucide-react";
import { toast } from "sonner";
import { addToCart, cartCount } from "../../lib/cart";
import NegotiateModal from "../../components/NegotiateModal";
import {
  CatalogLayoutShell, CatalogProductCard, CatalogTextTable, CatalogImage,
  FulfillBadges, BUYER_TESTIDS, useAttrFacets,
} from "../../components/catalog-shared";

const SEARCH_FIELDS = [
  { value: "all", label: "All fields" },
  { value: "name", label: "Name" },
  { value: "brand", label: "Brand" },
  { value: "sku", label: "SKU" },
  { value: "upc", label: "UPC" },
  { value: "product_type", label: "Type" },
  { value: "size", label: "Size" },
];
const SORT_OPTS = [
  { value: "featured", label: "Featured" },
  { value: "new", label: "New arrivals first" },
  { value: "top", label: "Top sellers" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "name_asc", label: "Name: A → Z" },
  { value: "name_desc", label: "Name: Z → A" },
];

export default function BuyerCatalog() {
  const nav = useNavigate();
  const { active: activeSupplier, clearSupplier, activeSellerName, activeSellerId } = useSupplier();
  const [products, setProducts] = useState([]);
  const [favs, setFavs] = useState([]);
  const [cartLen, setCartLen] = useState(cartCount());
  const [exportOpen, setExportOpen] = useState(false);

  // search & filters
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [badgeSet, setBadgeSet] = useState(new Set());
  const [cats, setCats] = useState(new Set());
  const [sellers, setSellers] = useState(new Set());
  const [fulfillment, setFulfillment] = useState("all");
  const [maxPrice, setMaxPrice] = useState(500);
  const [minMoq, setMinMoq] = useState(1);
  const [sort, setSort] = useState("featured");

  // selection & qty per product
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState({});
  const [unitMode, setUnitMode] = useState({});

  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("verqo:catalog:view") || "grid"; } catch { return "grid"; }
  });
  useEffect(() => { try { localStorage.setItem("verqo:catalog:view", viewMode); } catch (_e) { /* ignore */ } }, [viewMode]);

  // dialogs
  const [detail, setDetail] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [negotiate, setNegotiate] = useState(null);

  const load = () => Promise.all([api.get("/products/all"), api.get("/suppliers/favorites")]).then(([p, f]) => {
    setProducts(p.data); setFavs(f.data.map(x => x.id));
  });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const refresh = () => setCartLen(cartCount());
    window.addEventListener("verqo-cart-change", refresh);
    return () => window.removeEventListener("verqo-cart-change", refresh);
  }, []);

  const categories = useMemo(() => {
    const counts = {};
    products.forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return Object.entries(counts).map(([k, n]) => ({ key: k, count: n }));
  }, [products]);

  const brands = useMemo(() => {
    const set = new Set(products.map(p => p.seller_name).filter(Boolean));
    return [...set].sort();
  }, [products]);

  const { attrFilters, applyAttrFacets, attrSelKey } = useAttrFacets(products);

  const filtered = useMemo(() => {
    let r = [...products];
    if (activeSellerId) r = r.filter(p => p.seller_id === activeSellerId);
    else if (activeSellerName) {
      const tname = activeSellerName.toLowerCase();
      r = r.filter(p => (p.seller_name || "").toLowerCase() === tname);
    }
    if (badgeSet.size > 0) r = r.filter(p => badgeSet.has(p.badge));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(p => {
        if (searchField === "name")  return p.name?.toLowerCase().includes(q);
        if (searchField === "brand") return p.seller_name?.toLowerCase().includes(q);
        if (searchField === "sku")   return p.sku?.toLowerCase().includes(q);
        if (searchField === "upc")   return p.upc?.toLowerCase().includes(q);
        if (searchField === "product_type") return p.product_type?.toLowerCase().includes(q);
        if (searchField === "size") return String(p.size || "").toLowerCase().includes(q);
        return [p.name, p.seller_name, p.sku, p.upc, p.brand, p.category, p.sub_category, p.product_type, p.size, p.gender, p.condition, p.variant, p.ean, p.asin].some(v => v != null && String(v).toLowerCase().includes(q));
      });
    }
    if (cats.size > 0) r = r.filter(p => cats.has(p.category));
    if (sellers.size > 0) r = r.filter(p => sellers.has(p.seller_name));
    r = applyAttrFacets(r);
    if (fulfillment !== "all") r = r.filter(p => p.fulfillment === fulfillment || p.fulfillment === "both");
    r = r.filter(p => p.price <= maxPrice);
    r = r.filter(p => (p.moq || 1) >= minMoq);
    if (sort === "price_asc") r.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") r.sort((a, b) => b.price - a.price);
    else if (sort === "name_asc") r.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "name_desc") r.sort((a, b) => b.name.localeCompare(a.name));
    else if (sort === "new") r.sort((a, b) => (b.badge === "new_arrival" ? 1 : 0) - (a.badge === "new_arrival" ? 1 : 0));
    else if (sort === "top") r.sort((a, b) => (b.badge === "top_seller" ? 1 : 0) - (a.badge === "top_seller" ? 1 : 0));
    return r;
  }, [products, search, searchField, badgeSet, cats, sellers, applyAttrFacets, attrSelKey, fulfillment, maxPrice, minMoq, sort, activeSellerName, activeSellerId]);

  const getQty = (p) => qty[p.id] ?? p.moq;
  const setQ = (pid, n) => setQty(prev => ({ ...prev, [pid]: Math.max(1, n) }));
  const getMode = (pid) => unitMode[pid] || "unit";
  const setMode = (pid, m) => setUnitMode(prev => ({ ...prev, [pid]: m }));

  const addProduct = (p) => {
    const baseQty = getQty(p);
    const finalQty = getMode(p.id) === "case" ? baseQty * (p.case_size || p.moq * 2) : baseQty;
    if (finalQty < p.moq) return toast.error(`MOQ for ${p.name} is ${p.moq} units`);
    addToCart({
      product_id: p.id, name: p.name, qty: finalQty, price: p.price,
      seller_id: p.seller_id, seller_name: p.seller_name, image: p.image, moq: p.moq,
      tiers: p.tiers || [], map_price: p.map_price ?? null,
    });
    toast.success(`${finalQty}× ${p.name} added`);
  };

  const addAllSelected = () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return toast.error("Nothing selected");
    let n = 0;
    ids.forEach(id => {
      const p = products.find(x => x.id === id);
      if (!p) return;
      addToCart({
        product_id: p.id, name: p.name, qty: getQty(p), price: p.price,
        seller_id: p.seller_id, seller_name: p.seller_name, image: p.image, moq: p.moq,
        tiers: p.tiers || [], map_price: p.map_price ?? null,
      });
      n++;
    });
    setSelected({});
    toast.success(`${n} item${n !== 1 ? "s" : ""} added to cart`);
  };

  const exportData = (fmt) => {
    const rows = filtered.map(p => ({
      sku: p.sku, name: p.name, brand: p.seller_name, category: p.category,
      wholesale: p.price, retail: p.retail_price, moq: p.moq, stock: p.stock,
      fulfillment: p.fulfillment, upc: p.upc,
    }));
    const blob = fmt === "json"
      ? new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" })
      : new Blob([Object.keys(rows[0] || {}).join(",") + "\n" + rows.map(r => Object.values(r).join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `catalogue.${fmt === "json" ? "json" : "csv"}`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} products`);
    setExportOpen(false);
  };

  const parseBulk = () => {
    const lines = bulkText.split("\n").map(l => l.trim()).filter(Boolean);
    const matched = [];
    lines.forEach(line => {
      const [code, qtyStr] = line.split(",").map(s => s?.trim());
      const q = parseInt(qtyStr) || 1;
      const p = products.find(x => x.sku === code || x.upc === code);
      if (p) matched.push({ ...p, _qty: q });
    });
    if (matched.length === 0) return toast.error("No matches found");
    matched.forEach(p => addToCart({
      product_id: p.id, name: p.name, qty: p._qty, price: p.price,
      seller_id: p.seller_id, seller_name: p.seller_name, image: p.image, moq: p.moq,
      tiers: p.tiers || [], map_price: p.map_price ?? null,
    }));
    toast.success(`${matched.length} items pushed to cart`);
    setBulkOpen(false); setBulkText("");
    nav("/buyer/cart");
  };

  const reviewCart = () => nav("/buyer/cart");
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="min-h-screen" data-testid="buyer-catalog">
      {/* Sticky header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Apps · Catalog</div>
          <h1 className="display text-2xl mt-1 tracking-tight">Shop catalogue</h1>
          <div className="text-xs text-foreground/55 mt-0.5">{products.length} products · {brands.length} verified suppliers</div>
        </div>
        <div className="flex gap-2 items-center">
          {activeSupplier?.tenant_slug && (
            <a href={`/s/${activeSupplier.tenant_slug}/catalog`} target="_blank" rel="noopener noreferrer"
              data-testid="catalog-open-as-webpage"
              title="Open this supplier's catalog as a store webpage in a new tab"
              className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-foreground/60 hover:text-foreground mr-1 underline-offset-2 hover:underline">
              <Store className="w-3 h-3"/>Open as webpage
            </a>
          )}
          <a href="/buyer/settings#integrations" className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-foreground/60 hover:text-foreground mr-1 underline-offset-2 hover:underline" data-testid="catalog-to-integrations">
            <Plug className="w-3 h-3"/>Connect to your ERP / Shopify
          </a>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-upload" className="rounded-full h-10 px-4">
            <Upload className="w-4 h-4 mr-1.5"/>Bulk upload
          </Button>
          <div className="relative">
            <Button variant="outline" onClick={() => setExportOpen(v => !v)} data-testid="export-btn" className="rounded-full h-10 px-4">
              <Download className="w-4 h-4 mr-1.5"/>Export
            </Button>
            {exportOpen && (
              <div className="absolute right-0 mt-2 rounded-xl bg-white border border-border shadow-lg p-1 z-30 w-40">
                <button onClick={() => exportData("csv")} className="w-full text-left px-3 py-2 hover:bg-secondary rounded-md text-sm">Export as CSV</button>
                <button onClick={() => exportData("json")} className="w-full text-left px-3 py-2 hover:bg-secondary rounded-md text-sm">Export as JSON</button>
              </div>
            )}
          </div>
          <Button onClick={reviewCart} data-testid="open-cart" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">
            <ShoppingCart className="w-4 h-4 mr-1.5"/>Cart{cartLen > 0 && <span className="ml-1.5 chip bg-accent text-foreground border border-accent/40">{cartLen}</span>}
          </Button>
        </div>
      </div>

      <div className="page-shell">
        {!activeSupplier && (
          <div className="mb-5">
            <HubModeNotice page="Catalog"
              description="The catalog is built from your connected supplier's product feed. While you explore verqo, send POs through the Hub instead — works with any supplier, including ones not on verqo yet."/>
          </div>
        )}
        {activeSupplier && (
          <div className="mb-5 rounded-xl border p-4 flex items-center gap-4 flex-wrap"
            style={{
              background: (activeSupplier.branding?.primary_color || "#0a1f1c") + "08",
              borderColor: (activeSupplier.branding?.accent_color || "#e8ff3a") + "66",
            }}
            data-testid="supplier-scope-chip">
            <span className="w-10 h-10 rounded-lg grid place-items-center text-background font-bold display shrink-0 overflow-hidden"
              style={{ background: activeSupplier.branding?.primary_color || "#0a1f1c", color: activeSupplier.branding?.accent_color || "#e8ff3a" }}>
              {activeSupplier.branding?.logo_url
                ? <img src={activeSupplier.branding.logo_url} alt="" className="w-full h-full object-contain p-1"/>
                : (activeSupplier.tenant_name || "?").slice(0, 1)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Viewing supplier</div>
              <div className="display text-lg tracking-tight">{activeSupplier.tenant_name}</div>
            </div>
            <button onClick={() => clearSupplier()} data-testid="supplier-scope-clear"
              className="text-[12.5px] text-foreground/60 hover:text-foreground inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5"/>Back to hub
            </button>
          </div>
        )}

        <CatalogLayoutShell
          testids={BUYER_TESTIDS}
          search={search} setSearch={setSearch}
          searchField={searchField} setSearchField={setSearchField}
          searchFields={SEARCH_FIELDS} searchPlaceholder="Search products, brands, SKU, UPC…"
          badgeSet={badgeSet} setBadgeSet={setBadgeSet}
          sort={sort} setSort={setSort} sortOptions={SORT_OPTS}
          categories={categories} cats={cats} setCats={setCats} totalCount={products.length}
          fulfillment={fulfillment} setFulfillment={setFulfillment}
          maxPrice={maxPrice} setMaxPrice={setMaxPrice} minMoq={minMoq} setMinMoq={setMinMoq}
          brands={brands} sellers={sellers} setSellers={setSellers}
          attrFilters={attrFilters}
          viewMode={viewMode} setViewMode={setViewMode} filteredCount={filtered.length}
          mainHeaderExtra={selectedCount > 0 ? (
            <div className="flex gap-2 items-center">
              <span className="text-foreground/70">{selectedCount} selected</span>
              <Button size="sm" onClick={addAllSelected} data-testid="add-all-selected"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-8 px-3">
                Add all to cart <ArrowRight className="w-3 h-3 ml-1"/>
              </Button>
            </div>
          ) : null}>
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-foreground/45 border border-dashed border-border rounded-2xl">
              <div className="display text-xl">No matches</div>
              <p className="text-xs mt-1">Adjust your filters to see more products</p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="product-grid">
              {filtered.map(p => (
                <CatalogProductCard key={p.id} mode="buyer" view="grid" p={p}
                  selected={selected[p.id]} onSelect={(v) => setSelected(s => ({ ...s, [p.id]: v }))}
                  qty={getQty(p)} setQty={(n) => setQ(p.id, n)}
                  unitMode={getMode(p.id)} setMode={(m) => setMode(p.id, m)}
                  onAdd={() => addProduct(p)} onDetails={() => setDetail(p)} onNegotiate={() => setNegotiate(p)}/>
              ))}
            </div>
          ) : viewMode === "list" ? (
            <div className="space-y-2" data-testid="product-list">
              {filtered.map(p => (
                <CatalogProductCard key={p.id} mode="buyer" view="list" p={p}
                  selected={selected[p.id]} onSelect={(v) => setSelected(s => ({ ...s, [p.id]: v }))}
                  qty={getQty(p)} setQty={(n) => setQ(p.id, n)}
                  onAdd={() => addProduct(p)} onDetails={() => setDetail(p)} onNegotiate={() => setNegotiate(p)}/>
              ))}
            </div>
          ) : (
            <CatalogTextTable mode="buyer" rows={filtered}
              selected={selected} onSelect={(id, v) => setSelected(s => ({ ...s, [id]: v }))}
              getQty={getQty} setQty={setQ} onAdd={addProduct} onDetails={setDetail} onNegotiate={setNegotiate}/>
          )}
        </CatalogLayoutShell>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={v => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="display text-2xl tracking-tight">{detail.name}</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-2 gap-6 mt-2">
                <ProductGallery primary={detail.image} gallery={detail.gallery_images || []} alt={detail.name} name={detail.name}/>
                <div>
                  <div className="inline-flex items-center gap-2"><Building2 className="w-3 h-3 text-foreground/50"/><span className="text-sm">{detail.seller_name}</span></div>
                  <FulfillBadges f={detail.fulfillment} className="mt-2"/>
                  <div className="display text-4xl mt-4 tracking-tight">{money(detail.price)}</div>
                  {detail.retail_price && (
                    <div className="text-sm text-foreground/55 mt-1">
                      Retail <span className="line-through">{money(detail.retail_price)}</span>
                      <span className="ml-2 chip bg-accent/30 border border-accent/40 text-[10px]">{Math.round((1 - detail.price / detail.retail_price) * 100)}% margin</span>
                    </div>
                  )}
                  <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                    <DetailKV l="SKU" v={detail.sku}/>
                    <DetailKV l="UPC" v={detail.upc}/>
                    <DetailKV l="Category" v={detail.category}/>
                    <DetailKV l="Stock" v={`${detail.stock} units`}/>
                    <DetailKV l="MOQ" v={`${detail.moq} units`}/>
                    <DetailKV l="Case size" v={`${detail.case_size || detail.moq * 2}`}/>
                  </div>
                  <Button onClick={() => { addProduct(detail); setDetail(null); }} data-testid="detail-add"
                    className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                    Add to order <ArrowRight className="w-4 h-4 ml-1.5"/>
                  </Button>
                  <button onClick={() => { const d = detail; setDetail(null); setNegotiate(d); }} data-testid="detail-negotiate"
                    className="mt-2 w-full text-[12px] text-foreground/65 hover:text-foreground inline-flex items-center justify-center gap-1.5 py-2">
                    <MessageSquare className="w-3.5 h-3.5"/>Request a quote or negotiate
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <NegotiateModal open={!!negotiate} product={negotiate} onClose={() => setNegotiate(null)}/>

      {/* Bulk upload modal */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Bulk upload order</DialogTitle></DialogHeader>
          <p className="text-xs text-foreground/55 -mt-1">One item per line · format: <code className="mono bg-secondary px-1.5 py-0.5 rounded">SKU_or_UPC, qty</code></p>
          <textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
            placeholder={"GC-RS-30, 24\nNM-ETH-1, 48\nGC-LT-06, 12"}
            className="mt-3 w-full h-40 rounded-xl border border-border p-3 text-sm mono" data-testid="bulk-text"/>
          <Button onClick={parseBulk} data-testid="bulk-add" className="mt-4 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            Match &amp; push to cart <ArrowRight className="w-4 h-4 ml-1.5"/>
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailKV({ l, v }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">{l}</div>
      <div className="font-medium">{v || "—"}</div>
    </div>
  );
}

/* Detail-dialog gallery — hero + thumb strip with arrow nav */
function ProductGallery({ primary, gallery, alt, name }) {
  const images = useMemo(() => {
    const arr = [];
    if (primary) arr.push(primary);
    (gallery || []).forEach(g => { if (g && !arr.includes(g)) arr.push(g); });
    return arr;
  }, [primary, gallery]);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => { setActiveIdx(0); }, [primary]);

  if (images.length === 0) {
    return (
      <div className="aspect-square rounded-2xl bg-secondary overflow-hidden">
        <CatalogImage src={null} alt={alt} name={name}/>
      </div>
    );
  }

  const active = images[Math.min(activeIdx, images.length - 1)];
  const hasMultiple = images.length > 1;

  return (
    <div data-testid="product-gallery">
      <div className="relative aspect-square rounded-2xl bg-secondary overflow-hidden group">
        <CatalogImage src={active} alt={alt} name={name}/>
        {hasMultiple && (
          <>
            <button onClick={() => setActiveIdx(i => (i - 1 + images.length) % images.length)} data-testid="gallery-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow grid place-items-center text-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Previous image">
              <ChevronLeft className="w-4 h-4"/>
            </button>
            <button onClick={() => setActiveIdx(i => (i + 1) % images.length)} data-testid="gallery-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow grid place-items-center text-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Next image">
              <ChevronRight className="w-4 h-4"/>
            </button>
            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-foreground/65 text-background text-[10px] mono">{activeIdx + 1} / {images.length}</div>
          </>
        )}
      </div>
      {hasMultiple && (
        <div className="mt-2.5 flex gap-1.5 overflow-x-auto" data-testid="gallery-thumbs">
          {images.map((src, i) => (
            <button key={src + i} onClick={() => setActiveIdx(i)} data-testid={`gallery-thumb-${i}`}
              className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === activeIdx ? "border-foreground" : "border-transparent hover:border-foreground/30"}`} aria-label={`Image ${i + 1}`}>
              <img src={src} alt="" className="w-full h-full object-cover"/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
